
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Job } from '@/components/jobs/types';
import { toast } from 'sonner';
import { trackJobUpdated, trackJobStatusChanged } from '@/utils/analytics/jobTracking';

// Define the type for the updatable job data
type UpdatableJobData = Partial<Pick<Job, 'title' | 'company' | 'location' | 'status' | 'description' | 'skills' | 'experience_required' | 'requirements' | 'source_url' | 'salary_min' | 'salary_max' | 'employment_type'>>;

interface UpdateJobPayload {
  id: string;
  data: UpdatableJobData;
  previousJob?: Job; // For tracking status changes
}

const updateJob = async ({ id, data }: UpdateJobPayload) => {
  // Remove salary if it's present, as we are moving to salary_min and salary_max
  if ('salary' in data) {
    // @ts-ignore
    delete data.salary;
  }

  const { error } = await supabase
    .from('jobs')
    .update(data)
    .eq('id', id);

  if (error) {
    throw new Error(error.message);
  }
  return null;
};

export const useUpdateJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateJob,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobList'] });
      
      // Track the job update
      const updatedFields = Object.keys(variables.data);
      
      // Get job title from the updated data or previous job
      const jobTitle = variables.data.title || variables.previousJob?.title || 'Unknown Job';
      const company = variables.data.company || variables.previousJob?.company;
      
      trackJobUpdated({
        job_id: variables.id,
        title: jobTitle,
        updated_fields: updatedFields,
        company: company
      });
      
      // Track status change specifically if status was updated
      if (variables.data.status && variables.previousJob?.status && 
          variables.data.status !== variables.previousJob.status) {
        trackJobStatusChanged({
          job_id: variables.id,
          title: jobTitle,
          from_status: variables.previousJob.status,
          to_status: variables.data.status,
          company: company
        });
      }
    },
    onError: (error) => {
      toast.error(`Failed to update job: ${error.message}`);
    },
  });
};
